import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Lock, ArrowRight, Landmark, ShieldCheck } from 'lucide-react';
import PortalShell from '@/components/portal/PortalShell';
import PublicHero from '@/components/portal/PublicHero';
import { GIISDEC_CONTACT } from '@/lib/contact';

// Phase I public contact surface. Public enquiries use official GIISDEC contact channels;
// authenticated investors use the Portal Support Centre.

const Contact: React.FC = () => {

  return (
    <PortalShell>
      <PublicHero
        badge="Contact"
        title={<>Let&rsquo;s <span className="text-eds-mint">connect</span></>}
        subtitle="Questions about access, investment or data? Reach the GIISDEC team."
      />

      <section className="py-16 md:py-20 bg-eds-bg">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Assurance bar */}
          <div className="mb-8 rounded-eds-lg border border-eds-border bg-eds-surface shadow-eds-sm px-5 py-3.5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 text-sm">
            <span className="inline-flex items-center gap-2 text-eds-fg font-medium">
              <Mail className="w-4 h-4 text-eds-emerald-2" /> Official GIISDEC contact channels
            </span>
            <span className="hidden sm:block w-px h-4 bg-eds-border" aria-hidden="true" />
            <span className="inline-flex items-center gap-2 text-eds-muted-fg">
              <ShieldCheck className="w-4 h-4 text-eds-emerald-2" /> Handled under GIISDEC data governance
            </span>
            <span className="hidden sm:block w-px h-4 bg-eds-border" aria-hidden="true" />
            <span className="inline-flex items-center gap-2 text-eds-muted-fg">
              <MapPin className="w-4 h-4 text-eds-emerald-2" /> East Legon, Accra · Ghana
            </span>
          </div>

            <div className="max-w-3xl mx-auto">
          {/* Contact details column */}
          <div className="space-y-5">
            <div className="rounded-eds-lg border border-eds-border bg-eds-surface p-6 shadow-eds-sm">
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 flex-shrink-0 rounded-eds-md bg-eds-success/10 border border-eds-success/20 flex items-center justify-center text-eds-emerald-2">
                  <MapPin className="w-5 h-5" />
                </span>
                <div>
                  <h3 className="font-semibold text-eds-fg">{GIISDEC_CONTACT.head.label}</h3>
                  <p className="text-sm text-eds-muted-fg mt-1">{GIISDEC_CONTACT.head.address}</p>
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div className="rounded-eds-lg border border-eds-border bg-eds-surface p-6 shadow-eds-sm">
                <Mail className="w-5 h-5 text-eds-emerald-2" />
                <h3 className="font-semibold text-eds-fg mt-3 text-sm">Email</h3>
                <a href={GIISDEC_CONTACT.emailHref} className="text-sm text-eds-emerald-2 hover:underline break-all">
                  {GIISDEC_CONTACT.email}
                </a>
              </div>
              <div className="rounded-eds-lg border border-eds-border bg-eds-surface p-6 shadow-eds-sm">
                <Phone className="w-5 h-5 text-eds-emerald-2" />
                <h3 className="font-semibold text-eds-fg mt-3 text-sm">Phone</h3>
                <a href={GIISDEC_CONTACT.phoneHref} className="text-sm text-eds-emerald-2 hover:underline">
                  {GIISDEC_CONTACT.phoneDisplay}
                </a>
              </div>
            </div>

            <div className="rounded-eds-lg border border-eds-border bg-eds-surface p-6 shadow-eds-sm flex items-start gap-3">
              <span className="w-10 h-10 flex-shrink-0 rounded-eds-md bg-eds-success/10 border border-eds-success/20 flex items-center justify-center text-eds-emerald-2">
                <Clock className="w-5 h-5" />
              </span>
              <div>
                <h3 className="font-semibold text-eds-fg text-sm">Hours</h3>
                <p className="text-sm text-eds-muted-fg mt-1">Mon–Fri · 08:00–17:00 GMT</p>
              </div>
            </div>

              {/* Governed institutional-access notice — accounts are provisioned by GIISDEC;
                  requests use the official GIISDEC contact channels below. */}
            <div className="rounded-eds-lg border border-eds-border bg-eds-surface p-6 shadow-eds-sm flex items-start gap-3">
              <span className="w-10 h-10 flex-shrink-0 rounded-eds-md bg-eds-gold/12 border border-eds-gold/25 flex items-center justify-center text-eds-earth">
                <Landmark className="w-5 h-5" />
              </span>
              <div>
                <h3 className="font-semibold text-eds-fg text-sm">Government &amp; Agency Access</h3>
                <p className="text-sm text-eds-muted-fg mt-1">
                  Government and Agency Access is available to authorized institutional users.
                  Institutional accounts are provisioned by GIISDEC. Email{' '}
                  <a href={GIISDEC_CONTACT.emailHref} className="text-eds-emerald-2 hover:underline">
                    {GIISDEC_CONTACT.email}
                  </a>{' '}
                  to request access.
                </p>
              </div>
            </div>

            <div className="rounded-eds-lg border border-eds-border bg-eds-forest text-white p-6">
              <div className="flex items-center gap-2 text-eds-mint">
                <Lock className="w-4 h-4" />
                <h3 className="font-semibold text-sm">Investor Access</h3>
              </div>
              <Link
                to="/investor-login"
                className="inline-flex items-center gap-1.5 mt-2 text-sm font-semibold text-white hover:text-eds-mint"
              >
                Sign in to the portal <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          </div>
        </div>
      </section>
    </PortalShell>
  );
};

export default Contact;
